import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Upload, Fuel, Droplet, Truck, Building2, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { btn, card, tbl } from '../theme';

const STATUS_STYLE = {
  committed:  { color: '#16A34A', bg: '#DCFCE7', icon: <CheckCircle  size={13} /> },
  preview:    { color: '#1D4ED8', bg: '#EFF6FF', icon: <Clock        size={13} /> },
  cancelled:  { color: '#6B7280', bg: '#F3F4F6', icon: <XCircle      size={13} /> },
  failed:     { color: '#DC2626', bg: '#FEE2E2', icon: <XCircle      size={13} /> },
};

const currency = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function FuelReport() {
  const { user } = useAuth();
  const canIngest = user?.role === 'super_admin';
  const [month, setMonth] = useState('');
  const [ccFilter, setCcFilter] = useState('all');

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['fuel-summary', month],
    queryFn:  () => api.get('/fuel-report/summary', { params: month ? { month } : {} }),
  });

  const ccOptions = (summary?.by_cost_center || []).filter((row) => row.cc_id);

  const { data: ccDetail, isLoading: ccDetailLoading } = useQuery({
    queryKey: ['fuel-costcenter', ccFilter, month],
    queryFn:  () => api.get(`/fuel-report/costcenter/${ccFilter}`, { params: month ? { month } : {} }),
    enabled:  ccFilter !== 'all',
  });

  const ccDetailTotals = (ccDetail?.by_vehicle || []).reduce(
    (acc, r) => ({
      gallons: acc.gallons + Number(r.total_gallons || 0),
      amount:  acc.amount + Number(r.total_amount || 0),
      transactions: acc.transactions + Number(r.transaction_count || 0),
    }),
    { gallons: 0, amount: 0, transactions: 0 }
  );

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Fuel"
        title="Fuel Report"
        subtitle="Spend and usage from ingested fuel card statements, resolved to vehicle and cost center via the fuel card mapping table."
      />

      <div style={s.filterRow}>
        <div style={s.filterGroup}>
          <label style={s.label}>Month</label>
          <input
            type="month"
            style={s.monthInput}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </div>
        <div style={s.filterGroup}>
          <label style={s.label}>Cost Center</label>
          <select style={s.monthInput} value={ccFilter} onChange={(e) => setCcFilter(e.target.value)}>
            <option value="all">All Cost Centers</option>
            {ccOptions.map((row) => (
              <option key={row.cc_id} value={row.cc_id}>{row.code} — {row.name}</option>
            ))}
          </select>
        </div>
        {(month || ccFilter !== 'all') && (
          <button style={s.clearBtn} onClick={() => { setMonth(''); setCcFilter('all'); }}>Clear filters</button>
        )}
      </div>

      {ccFilter === 'all' ? (
        summaryLoading ? <LoadingSpinner /> : (
          <>
            <div style={s.kpiRow}>
              <KPICard icon={<Droplet size={16} />} label="Gallons" value={Number(summary?.kpis?.total_gallons || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} />
              <KPICard icon={<Fuel size={16} />} label="Total Spend" value={currency(summary?.kpis?.total_amount)} />
              <KPICard icon={<Truck size={16} />} label="Vehicles" value={summary?.kpis?.vehicle_count ?? 0} />
              <KPICard icon={<Building2 size={16} />} label="Cost Centers" value={summary?.kpis?.cost_center_count ?? 0} />
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>By Cost Center</div>
              <table style={s.table}>
                <thead>
                  <tr>
                    {['Cost Center', 'Vehicles', 'Gallons', 'Spend', 'Transactions'].map((h) => (
                      <th key={h} style={{ ...s.th, textAlign: h === 'Cost Center' ? 'left' : 'right' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(summary?.by_cost_center || []).map((row) => (
                    <tr key={row.cc_id || 'unassigned'} style={s.tr}>
                      <td style={s.td}>
                        {row.cc_id ? (
                          <button style={s.linkBtn} onClick={() => setCcFilter(row.cc_id)}>{row.code} — {row.name}</button>
                        ) : 'Unmapped'}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{row.vehicle_count}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{Number(row.total_gallons).toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{currency(row.total_amount)}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{row.transaction_count}</td>
                    </tr>
                  ))}
                  {!(summary?.by_cost_center || []).length && (
                    <tr><td colSpan={5} style={s.empty}>No fuel data for this period yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )
      ) : (
        ccDetailLoading ? <LoadingSpinner /> : (
          <>
            <div style={s.kpiRow}>
              <KPICard icon={<Droplet size={16} />} label="Gallons" value={ccDetailTotals.gallons.toLocaleString('en-US', { maximumFractionDigits: 0 })} />
              <KPICard icon={<Fuel size={16} />} label="Total Spend" value={currency(ccDetailTotals.amount)} />
              <KPICard icon={<Truck size={16} />} label="Vehicles" value={(ccDetail?.by_vehicle || []).length} />
              <KPICard icon={<Building2 size={16} />} label="Cost Center" value={ccDetail?.cost_center?.code || '—'} />
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>{ccDetail?.cost_center?.name || 'Cost Center'} — By Vehicle</div>
              <table style={s.table}>
                <thead>
                  <tr>
                    {['Vehicle', 'Gallons', 'Spend', 'Transactions'].map((h) => (
                      <th key={h} style={{ ...s.th, textAlign: h === 'Vehicle' ? 'left' : 'right' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(ccDetail?.by_vehicle || []).map((row) => (
                    <tr key={row.vehicle_id || 'unassigned'} style={s.tr}>
                      <td style={s.td}>{row.vehicle_id || 'Unassigned'}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{Number(row.total_gallons).toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{currency(row.total_amount)}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{row.transaction_count}</td>
                    </tr>
                  ))}
                  {!(ccDetail?.by_vehicle || []).length && (
                    <tr><td colSpan={4} style={s.empty}>No fuel data for this cost center/period.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )
      )}

      {canIngest && <IngestionPanel />}
    </div>
  );
}

function KPICard({ icon, label, value }) {
  return (
    <div style={s.kpiCard}>
      <div style={s.kpiIcon}>{icon}</div>
      <div>
        <div style={s.kpiValue}>{value}</div>
        <div style={s.kpiLabel}>{label}</div>
      </div>
    </div>
  );
}

function IngestionPanel() {
  const qc = useQueryClient();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { batch_id, preview: {...summary} }
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const { data: batches = [], isLoading: batchesLoading } = useQuery({
    queryKey: ['fuel-batches'],
    queryFn:  () => api.get('/fuel-report/batches'),
    refetchInterval: 10000,
  });

  const uploadMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file);
      return api.upload('/fuel-report/upload', fd, setProgress);
    },
    onSuccess: (data) => {
      setPreview(data);
      toast.success(`Parsed ${data.preview.total_rows} rows — review before committing.`);
      qc.invalidateQueries(['fuel-batches']);
    },
    onError: (err) => {
      toast.error(err.message || 'Upload failed.');
      setProgress(0);
    },
  });

  const confirmMutation = useMutation({
    mutationFn: () => api.commit('/fuel-report/upload/confirm', { batch_id: preview.batch_id }),
    onSuccess: () => {
      toast.success('Batch committed — data is now visible in the report.');
      resetUpload();
      qc.invalidateQueries(['fuel-batches']);
      qc.invalidateQueries(['fuel-summary']);
    },
    onError: (err) => toast.error(err.message || 'Failed to commit batch.'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post('/fuel-report/upload/cancel', { batch_id: preview.batch_id }),
    onSuccess: () => {
      toast.success('Batch discarded.');
      resetUpload();
      qc.invalidateQueries(['fuel-batches']);
    },
    onError: (err) => toast.error(err.message || 'Failed to discard batch.'),
  });

  function resetUpload() {
    setFile(null);
    setPreview(null);
    setProgress(0);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div style={s.ingestSection}>
      <div style={s.hd2}>
        <h2 style={s.title2}>Ingest Fuel Statement</h2>
        <p style={s.sub}>Upload a fuel card statement export. Rows are matched against the fuel card mapping table before you commit — unmatched cards are called out so nothing is silently dropped.</p>
      </div>

      <div style={s.card}>
        {!preview ? (
          <>
            <div
              style={{ ...s.dropZone, borderColor: file ? '#86EFAC' : '#D1D5DB' }}
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files[0];
                if (f) setFile(f);
              }}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={(e) => setFile(e.target.files[0] || null)}
              />
              <Upload size={24} color={file ? '#16A34A' : '#9CA3AF'} />
              <div style={s.dropText}>
                {file ? (
                  <><strong style={{ color: '#16A34A' }}>{file.name}</strong><br /><span style={{ color: '#9CA3AF' }}>{(file.size / 1024).toFixed(0)} KB</span></>
                ) : (
                  <><strong>Click to select</strong> or drag &amp; drop<br /><span style={{ color: '#9CA3AF' }}>.xlsx, .xls, or .csv</span></>
                )}
              </div>
            </div>

            {uploadMutation.isPending && (
              <div style={s.progressWrap}>
                <div style={s.progressTrack}>
                  <div style={{ ...s.progressFill, width: `${progress}%` }} />
                </div>
                <span style={s.progressLabel}>{progress}%</span>
              </div>
            )}

            <button
              style={{ ...s.uploadBtn, opacity: file && !uploadMutation.isPending ? 1 : 0.5 }}
              disabled={!file || uploadMutation.isPending}
              onClick={() => uploadMutation.mutate()}
            >
              {uploadMutation.isPending ? 'Parsing…' : '↑ Upload and Preview'}
            </button>
          </>
        ) : (
          <div>
            <div style={s.previewGrid}>
              <PreviewStat label="Total Rows" value={preview.preview.total_rows} />
              <PreviewStat label="Matched" value={preview.preview.matched_rows} />
              <PreviewStat label="Unmatched" value={preview.preview.unmatched_rows} warn={preview.preview.unmatched_rows > 0} />
              <PreviewStat label="Total Spend" value={currency(preview.preview.total_amount)} />
            </div>

            {preview.preview.unmatched_rows > 0 && (
              <div style={s.warningBox}>
                <AlertTriangle size={14} color="#92400E" />
                <span>{preview.preview.unmatched_rows} row(s) had a card not found in the Fuel Card Mapping table: {preview.preview.unmatched_card_ids.slice(0, 10).join(', ')}{preview.preview.unmatched_card_ids.length > 10 ? '…' : ''}. They'll still be committed but won't show under any cost center until mapped.</span>
              </div>
            )}

            {preview.preview.warning_count > 0 && (
              <div style={s.warningBox}>
                <AlertTriangle size={14} color="#92400E" />
                <span>{preview.preview.warning_count} row(s) skipped — see server log for details.</span>
              </div>
            )}

            <div style={s.previewActions}>
              <button style={s.cancelBtn} onClick={() => cancelMutation.mutate()} disabled={cancelMutation.isPending || confirmMutation.isPending}>
                Discard
              </button>
              <button style={s.confirmBtn} onClick={() => confirmMutation.mutate()} disabled={cancelMutation.isPending || confirmMutation.isPending}>
                {confirmMutation.isPending ? 'Committing…' : 'Commit Batch'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={s.logSection}>
        <div style={s.logTitle}>Recent Ingestion History</div>
        {batchesLoading ? <LoadingSpinner /> : (
          <table style={s.table}>
            <thead>
              <tr>
                {['File', 'Source', 'Status', 'Rows', 'Matched', 'Created'].map((h) => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => {
                const st = STATUS_STYLE[b.status] || STATUS_STYLE.preview;
                return (
                  <tr key={b.id} style={s.tr}>
                    <td style={s.td} title={b.file_name}>{b.file_name?.slice(-40) || '—'}</td>
                    <td style={s.td}>{b.source === 'ftp' ? '⟳ FTP' : '↑ Manual'}</td>
                    <td style={s.td}>
                      <span style={{ ...s.statusPill, color: st.color, background: st.bg }}>
                        {st.icon} {b.status}
                      </span>
                    </td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{b.row_count ?? '—'}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{b.matched_row_count ?? '—'}</td>
                    <td style={s.td}>{new Date(b.created_at).toLocaleString()}</td>
                  </tr>
                );
              })}
              {!batches.length && (
                <tr><td colSpan={6} style={s.empty}>No ingestion history yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PreviewStat({ label, value, warn }) {
  return (
    <div style={s.previewStat}>
      <div style={{ ...s.previewStatValue, color: warn ? '#92400E' : '#111827' }}>{value}</div>
      <div style={s.previewStatLabel}>{label}</div>
    </div>
  );
}

const s = {
  page:        { maxWidth: '1000px', margin: '0 auto' },
  hd2:         { marginBottom: '16px' },
  title2:      { fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '4px' },
  sub:         { fontSize: '13px', color: '#6B7280', lineHeight: 1.6 },
  filterRow:   { display: 'flex', alignItems: 'flex-end', gap: '14px', marginBottom: '20px', flexWrap: 'wrap' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label:       { fontSize: '11px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' },
  monthInput:  { padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', color: '#374151', fontFamily: 'inherit', minWidth: '160px' },
  clearBtn:    { ...btn.secondaryXs },
  linkBtn:     { ...btn.link, textAlign: 'left' },
  kpiRow:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' },
  kpiCard:     { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '16px 18px', display: 'flex', alignItems: 'center', gap: '12px' },
  kpiIcon:     { width: '34px', height: '34px', borderRadius: '8px', background: '#EFF6FF', color: '#1D4ED8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  kpiValue:    { fontSize: '18px', fontWeight: 700, color: '#111827' },
  kpiLabel:    { fontSize: '11px', color: '#6B7280', marginTop: '1px' },
  card:        { ...card.base, overflow: 'hidden', marginBottom: '20px' },
  cardTitle:   { ...card.head },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:          { ...tbl.th, ...tbl.th },
  tr:          { ...tbl.tr, ...tbl.tr },
  td:          { ...tbl.td, ...tbl.td },
  empty:       { textAlign: 'center', padding: '24px', color: '#9CA3AF' },
  ingestSection: { marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' },
  dropZone: {
    border: '2px dashed', borderRadius: '10px', padding: '32px',
    textAlign: 'center', cursor: 'pointer', marginBottom: '16px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
    transition: 'border-color 0.2s',
  },
  dropText:     { fontSize: '13px', color: '#374151', lineHeight: 1.6 },
  progressWrap: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
  progressTrack:{ flex: 1, height: '5px', background: '#E5E7EB', borderRadius: '4px', overflow: 'hidden' },
  progressFill: { height: '100%', background: '#1B2A4A', borderRadius: '4px', transition: 'width 0.2s' },
  progressLabel:{ fontSize: '11px', fontWeight: 600, color: '#374151', minWidth: '30px' },
  uploadBtn: {
    padding: '10px 20px', fontSize: '13px', fontWeight: 600, border: 'none', borderRadius: '8px',
    background: '#1B2A4A', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', transition: 'opacity 0.15s',
  },
  previewGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', padding: '20px', paddingBottom: '0' },
  previewStat: { textAlign: 'center' },
  previewStatValue: { fontSize: '20px', fontWeight: 700 },
  previewStatLabel: { fontSize: '11px', color: '#6B7280', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  warningBox: {
    display: 'flex', alignItems: 'flex-start', gap: '8px', margin: '16px 20px 0',
    padding: '10px 12px', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '8px',
    fontSize: '12px', color: '#92400E', lineHeight: 1.5,
  },
  previewActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '20px' },
  cancelBtn: {
    padding: '8px 16px', fontSize: '12px', border: '1px solid #D1D5DB',
    borderRadius: '6px', background: '#fff', color: '#6B7280', cursor: 'pointer', fontFamily: 'inherit',
  },
  confirmBtn: {
    padding: '8px 16px', fontSize: '12px', fontWeight: 600, border: 'none',
    borderRadius: '6px', background: '#1B2A4A', color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
  },
  logSection:   { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' },
  logTitle:     { fontSize: '13px', fontWeight: 600, color: '#374151', padding: '14px 18px', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB' },
  statusPill:   { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 500 },
};
